/**
 * グラフユーティリティ関数
 */

/**
 * 線形スケールを作成
 */
export function createLinearScale(
  domain: [number, number],
  range: [number, number]
): (value: number) => number {
  const [domainMin, domainMax] = domain;
  const [rangeMin, rangeMax] = range;
  const domainSize = domainMax - domainMin;
  const rangeSize = rangeMax - rangeMin;

  if (domainSize === 0) {
    return () => rangeMin;
  }

  return (value: number) => {
    const ratio = (value - domainMin) / domainSize;
    return rangeMin + ratio * rangeSize;
  };
}

/**
 * カテゴリカルスケールを作成
 */
export function createCategoryScale(
  categories: string[],
  range: [number, number]
): (value: string) => number {
  const [rangeMin, rangeMax] = range;
  const categoryCount = categories.length;
  const step = categoryCount > 1 ? (rangeMax - rangeMin) / (categoryCount - 1) : 0;

  return (value: string) => {
    const index = categories.indexOf(value);
    if (index === -1) return rangeMin;
    return rangeMin + index * step;
  };
}

/**
 * 棒グラフのカテゴリX軸帯の幅を、設定の上限内で実際の棒列幅に合わせて短くする。
 * gapPx / effectiveBarWidth は widthCap を availableWidth として既に求めてあること。
 */
export function shrinkBarCategoryAxisLength(
  barCount: number,
  widthCap: number,
  gapPx: number,
  effectiveBarWidth: number
): number {
  if (barCount <= 0 || widthCap <= 0) {
    return widthCap;
  }
  const lastBarRightRel =
    gapPx + barCount * effectiveBarWidth + (barCount - 1) * gapPx;
  const withEndMargin = lastBarRightRel + gapPx;
  return Math.min(widthCap, withEndMargin);
}

/**
 * カテゴリ帯の左端 X。
 * 棒列に合わせて縮めた帯は左寄せ（Y軸が中央に浮いて左が砂漠になるのを防ぐ）。
 * lengthPercent 上限にちょうど乗った短い帯だけ、従来どおり中央寄せ。
 */
export function resolveCategoryBandStartX(
  plotLeft: number,
  plotWidth: number,
  axisLength: number,
  axisLengthMax: number
): number {
  if (!(plotWidth > 0) || !(axisLength > 0)) return plotLeft;
  if (axisLength < axisLengthMax - 0.5) return plotLeft;
  return plotLeft + Math.max(0, (plotWidth - axisLength) / 2);
}

/**
 * 目盛・ドメイン計算用: `[a, b]` を昇順の端点に正規化（`max < min` の反転ドメインでも数値範囲は同一）
 */
function sortedDomainBounds(domain: [number, number]): [number, number] {
  const lo = Math.min(domain[0], domain[1]);
  const hi = Math.max(domain[0], domain[1]);
  return [lo, hi];
}

/**
 * 美しい目盛りを計算
 */
export function calculateNiceTicks(domain: [number, number], tickCount: number): number[] {
  const [min, max] = sortedDomainBounds(domain);
  const range = max - min;

  if (range === 0) {
    return [min];
  }

  // 粗い目盛り間隔を計算
  const rawStep = range / tickCount;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalizedStep = rawStep / magnitude;

  // 1, 2, 5, 10のいずれかに正規化
  let step: number;
  if (normalizedStep <= 1) {
    step = 1 * magnitude;
  } else if (normalizedStep <= 2) {
    step = 2 * magnitude;
  } else if (normalizedStep <= 5) {
    step = 5 * magnitude;
  } else {
    step = 10 * magnitude;
  }

  // 目盛りの開始点を計算（stepの倍数に丸める）
  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;

  // 目盛りを生成
  const ticks: number[] = [];
  for (let value = start; value <= end; value += step) {
    // 浮動小数点の誤差を考慮
    const rounded = Math.round(value / step) * step;
    if (rounded >= min && rounded <= max) {
      ticks.push(rounded);
    }
  }

  return ticks;
}

/**
 * きれいなドメイン（端を丸める）
 */
export function calculateNiceDomain(domain: [number, number], tickCount: number): [number, number] {
  const [min, max] = sortedDomainBounds(domain);
  const range = max - min;

  if (range === 0) {
    return [min, max];
  }

  const rawStep = range / tickCount;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalizedStep = rawStep / magnitude;

  let step: number;
  if (normalizedStep <= 1) {
    step = 1 * magnitude;
  } else if (normalizedStep <= 2) {
    step = 2 * magnitude;
  } else if (normalizedStep <= 5) {
    step = 5 * magnitude;
  } else {
    step = 10 * magnitude;
  }

  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;

  return [start, end];
}

/**
 * 指定した間隔で目盛りを計算
 */
export function calculateTicksByInterval(domain: [number, number], interval: number | null): number[] {
  const [min, max] = sortedDomainBounds(domain);
  const range = max - min;

  if (range === 0) {
    return [min];
  }

  // 間隔が指定されていない場合は自動計算
  if (interval === null || interval <= 0) {
    return calculateNiceTicks([min, max], 8);
  }

  // 目盛りの開始点を計算（intervalの倍数に丸める）
  const start = Math.floor(min / interval) * interval;
  const end = Math.ceil(max / interval) * interval;

  // 目盛りを生成
  const ticks: number[] = [];
  for (let value = start; value <= end + 0.0001; value += interval) {
    // 浮動小数点の誤差を考慮
    const rounded = Math.round(value / interval) * interval;
    if (rounded >= min - 0.0001 && rounded <= max + 0.0001) {
      ticks.push(rounded);
    }
  }

  return ticks;
}

/**
 * 指定した間隔で目盛りを計算（最小値から開始）
 */
export function calculateTicksFromMinByInterval(
  domain: [number, number],
  interval: number | null
): number[] {
  const [min, max] = sortedDomainBounds(domain);
  const range = max - min;

  if (range === 0) {
    return [min];
  }

  if (interval === null || interval <= 0) {
    return calculateNiceTicks([min, max], 8);
  }

  const ticks: number[] = [];
  for (let value = min; value <= max + 0.0001; value += interval) {
    const rounded = Math.round(value * 1000000) / 1000000;
    ticks.push(rounded);
  }

  return ticks;
}

/**
 * Y軸のドメインを計算（エラーバーを考慮）
 */
export function getYDomainForBars(data: Array<{ value: number; errorBar?: { lower: number; upper: number } }>): [number, number] {
  if (data.length === 0) {
    return [0, 1];
  }

  let min = Infinity;
  let max = -Infinity;

  data.forEach((item) => {
    if (item.errorBar) {
      min = Math.min(min, item.errorBar.lower);
      max = Math.max(max, item.errorBar.upper);
    } else {
      min = Math.min(min, item.value);
      max = Math.max(max, item.value);
    }
  });

  // 棒グラフは 0 を基線にする（棒は常にプロット下端＝ドメイン下端から描画されるため、
  // 0 を含めないと棒の長さが値に比例せず誤解を招く truncated 軸になる）。
  // 正の値のみなら下端を 0 に、負の値のみなら上端を 0 に固定する。
  // 軸を切り詰めたい場合は yAxis.minValue / maxValue の明示指定で上書きできる。
  const baselineMin = Math.min(0, min);
  const baselineMax = Math.max(0, max);

  const [niceMin, niceMax] = calculateNiceDomain([baselineMin, baselineMax], 5);
  return [niceMin, niceMax];
}

/**
 * カラーパレット（論文品質）
 */
export const KEISUKE_COLORS = [
  '#1f77b4', // 青
  '#ff7f0e', // オレンジ
  '#2ca02c', // 緑
  '#d62728', // 赤
  '#9467bd', // 紫
  '#8c564b', // 茶色
  '#e377c2', // ピンク
  '#7f7f7f', // グレー
];

/**
 * 色を取得（循環的に使用）
 */
export function getColor(index: number): string {
  return KEISUKE_COLORS[index % KEISUKE_COLORS.length];
}

/**
 * SVG/CSS の font-family 用。値を trim し、全体が1組の引用符だけで囲まれた単一トークンのときだけ外す。
 * 旧処理（先頭・末尾1文字ずつ ' " を削除）は `"Times New Roman", Times, serif` 等の正当な CSS を壊し、
 * フォント切替が効かないように見えていた。
 */
export function normalizeChartFontFamilyValue(value: string): string {
  const v = value.trim();
  if (v.length < 2) return v;
  const first = v[0];
  const last = v[v.length - 1];
  if (first === '"' && last === '"' && !v.slice(1, -1).includes('"')) {
    return v.slice(1, -1);
  }
  if (first === "'" && last === "'" && !v.slice(1, -1).includes("'")) {
    return v.slice(1, -1);
  }
  return v;
}

/**
 * 軸タイトル・目盛り・凡例など SVG/CSS の font-family にそのまま渡す候補。
 * （保存値が一覧にない場合はプルダウンに「保存済み」として別 option で表示する）
 */
export const KEISUKE_CHART_FONT_FAMILY_PRESETS: readonly { value: string; label: string }[] = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica / Arial' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: '"Courier New", Courier, monospace', label: 'Courier New' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
  {
    value: '"Segoe UI", Roboto, "Helvetica Neue", Cantarell, "Fira Sans", Arial, sans-serif',
    label: 'Segoe UI / Roboto / Linux',
  },
  { value: '"Hiragino Sans", "Hiragino Kaku Gothic ProN", Meiryo, sans-serif', label: 'Hiragino / Meiryo（ゴシック）' },
  { value: 'Meiryo, "Hiragino Sans", "Hiragino Kaku Gothic ProN", sans-serif', label: 'メイリオ' },
  { value: '"Yu Mincho", "Hiragino Mincho ProN", serif', label: 'Yu Mincho / 明朝系' },
];

/**
 * 表の群・因子セルからカテゴリ名を得る。空欄・未入力・空白のみは null（グラフに項目を増やさない）
 */
export function keisukeCategoryLabelFromCell(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  return s.length > 0 ? s : null;
}

/**
 * 表セルを数値として解釈（文字列の数値も可）。空・非数は null。
 * グラフと検定で `typeof === 'number'` だけにすると、貼り付け直後など文字列のままのセルが無視され不整合になる。
 */
export function keisukeNumericCellValue(raw: unknown): number | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

